<template>
  <v-container fluid fill-height class="login-page">
    <v-layout align-center justify-center>
      <v-flex xs12 sm8 md5 lg4 xl3 class="login-flex">
        <v-card
          class="login-card"
          style="box-shadow: rgba(50, 50, 93, 0.25) 0px 50px 100px -20px, rgba(0, 0, 0, 0.3) 0px 30px 60px -30px, rgba(10, 37, 64, 0.35) 0px -2px 6px 0px inset;"
        >
          <!-- Header Toolbar -->
          <v-toolbar color="primary" dark flat class="login-toolbar">
            <div class="top-title-group">
              <span class="top-label">National University - EMS</span>
              <span class="top-title">{{ pageTitle }}</span>
            </div>
          </v-toolbar>

          <!-- Logo Badge — overlaps toolbar bottom-right -->
          <div class="logo-badge-wrapper">
            <div class="logo-badge">
              <v-img
                :src="require('@/assets/nu_logos/nu_logo_r.png')"
                contain
                class="logo-img"
              ></v-img>
            </div>
          </div>

          <!-- Progress bar -->
          <v-progress-linear
            v-if="loader"
            :indeterminate="true"
            color="primary"
            height="3"
            class="ma-0"
          ></v-progress-linear>
          <div v-else style="height: 3px;"></div>

          <v-card-text class="login-card-text">
            <!-- Error alert -->
            <v-alert
              v-if="errorMessage"
              type="error"
              dismissible
              dense
              class="mb-3 caption error-alert"
              @input="errorMessage = ''"
              >{{ errorMessage }}</v-alert
            >

            <v-form ref="loginForm" lazy-validation style="margin-top: -30px;">
              <!-- Email -->
              <div class="field-label mb-1">Email</div>
              <v-text-field
                v-model="user.email"
                name="login"
                type="text"
                placeholder="Enter your email"
                solo
                flat
                dense
                background-color="#F5F6FA"
                class="login-field"
                :rules="rules.username"
                validate-on-blur
                @keyup.enter="checkUser"
              >
                <template v-slot:prepend-inner>
                  <v-icon small color="grey darken-1" class="mr-1"
                    >person</v-icon
                  >
                </template>
              </v-text-field>

              <!-- Password -->
              <div class="field-label mb-1 mt-1">Password</div>
              <v-text-field
                v-model="user.password"
                id="password"
                name="password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Enter your password"
                solo
                flat
                dense
                background-color="#F5F6FA"
                class="login-field"
                :rules="rules.password"
                validate-on-blur
                @keyup.enter="checkUser"
              >
                <template v-slot:prepend-inner>
                  <v-icon small color="grey darken-1" class="mr-1">lock</v-icon>
                </template>
                <template v-slot:append>
                  <v-icon
                    small
                    color="grey"
                    style="cursor: pointer;"
                    @click="showPassword = !showPassword"
                    :aria-label="
                      showPassword ? 'Hide password' : 'Show password'
                    "
                    role="button"
                    tabindex="0"
                    @keyup.enter="showPassword = !showPassword"
                    @keyup.space="showPassword = !showPassword"
                    >{{
                      showPassword ? "visibility_off" : "visibility"
                    }}</v-icon
                  >
                </template>
              </v-text-field>

              <!-- Security Verification -->
              <div class="field-label captcha-label mb-1 mt-2">
                Security Verification
              </div>

              <!-- Captcha row -->
              <div
                class="captcha-row"
                :class="{ 'captcha-row--error': captchaError }"
              >
                <!-- Captcha image -->
                <div
                  class="captcha-image-box"
                  :class="{ 'captcha-image-box--error': captchaError }"
                  role="img"
                  aria-label="Math captcha image"
                >
                  <img
                    v-if="captcha.img"
                    :src="captcha.img"
                    class="captcha-img"
                    alt="Solve this math problem to continue"
                  />
                  <span v-else class="captcha-placeholder">Loading...</span>
                </div>

                <span
                  class="captcha-equals grey--text text--darken-1"
                  aria-hidden="true"
                  >=</span
                >

                <!-- Captcha answer — no rules, hide-details, manual validation -->
                <v-text-field
                  v-model="user.captcha"
                  placeholder="Answer"
                  solo
                  flat
                  dense
                  background-color="#F5F6FA"
                  class="captcha-input login-field"
                  :class="{ 'captcha-input--error': captchaError }"
                  hide-details
                  aria-label="Enter the answer to the math problem"
                  @input="captchaError = false"
                  @keyup.enter="checkUser"
                ></v-text-field>

                <!-- Refresh -->
                <v-btn
                  icon
                  small
                  fab
                  color="primary"
                  class="captcha-refresh"
                  @click="loadCaptcha"
                  title="Refresh captcha"
                  aria-label="Refresh captcha"
                >
                  <v-icon small>refresh</v-icon>
                </v-btn>
              </div>

              <!-- Captcha error message — sits below the full row -->
              <div v-if="captchaError" class="captcha-error-msg">
                <v-icon x-small color="error" class="captcha-error-icon"
                  >error_outline</v-icon
                >
                {{ captchaErrorText }}
              </div>

              <!-- Hint box -->
              <div
                class="hint-box"
                role="note"
                :class="{ 'hint-box--pushed': captchaError }"
              >
                <v-icon small class="hint-icon">lightbulb_outline</v-icon>
                <span class="hint-text">
                  <strong>Hint:</strong> Solve the math problem to prove you are
                  human and complete login.
                </span>
              </div>
            </v-form>
          </v-card-text>

          <v-divider class="mx-4"></v-divider>

          <v-card-actions class="login-card-actions">
            <v-layout column align-center>
              <v-btn
                color="primary"
                block
                depressed
                :loading="loader"
                :disabled="loader"
                class="login-btn white--text"
                type="button"
                @click="checkUser"
              >
                <v-icon left>account_circle</v-icon>
                Login
              </v-btn>
            </v-layout>
          </v-card-actions>
        </v-card>
        <!-- Instruction card (center-user only) -->
        <v-card
          v-if="module === 'center-user'"
          flat
          class="mt-3 instruction-card"
        >
          <v-card-text class="py-2 px-3">
            <table class="instruction-table">
              <tbody>
                <tr>
                  <td
                    class="caption grey--text text--darken-2 instruction-text"
                  >
                    Instruction for Practical &amp; Viva mark entry
                  </td>
                  <td class="text-right instruction-link-cell">
                    <a
                      class="caption primary--text instruction-link"
                      :href="
                        getBaseUrl +
                          '/Instruction for PV mark entry updated.pdf'
                      "
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <v-icon x-small color="primary" class="mr-1"
                        >file_download</v-icon
                      >
                      Download
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
    <v-snackbar
      v-model="snackbar"
      top
      right
      :timeout="6000"
      color="red darken-2"
      content-class="custom-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-3" color="white">
          error_outline
        </v-icon>

        <div class="snackbar-message">
          {{ errorLogMsg.message }}
        </div>
      </div>

      <v-btn slot="action" icon dark @click="snackbar = false">
        <v-icon>close</v-icon>
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import commonGet from "@/mixin/common_getters_mixin";
import common_login_getters from "@/mixin/common_login_getters";

export default {
  name: "LoginPage",
  mixins: [commonGet, common_login_getters],

  data() {
    return {
      snackbar: false,
      module: "",
      loader: false,
      errorMessage: "",
      showPassword: false,
      captchaError: false,
      captchaErrorText: "Security Verification is required.",

      user: {
        email: "",
        password: "",
        captcha: "",
        key: "",
      },

      rules: {
        username: [
          (v) => !!v || "Email is required",
          (v) => (v && v.trim().length > 0) || "Email cannot be blank",
          (v) =>
            (v && v.trim().length >= 3) ||
            "Email must be at least 3 characters",
          (v) =>
            (v && v.trim().length <= 50) ||
            "Email must be 50 characters or fewer",
        ],
        password: [
          (v) => !!v || "Password is required",
          (v) => (v && v.trim().length > 0) || "Password cannot be blank",
          (v) =>
            (v && v.length >= 4) || "Password must be at least 4 characters",
          (v) =>
            (v && v.length <= 100) ||
            "Password must be 100 characters or fewer",
        ],
      },
    };
  },

  computed: {
    getBaseUrl() {
      return this.$store.getters.getBaseUrl + "/user-manual";
    },
    captcha() {
      return this.$store.getters.getCaptcha || {};
    },
    pageTitle() {
      const titles = {
        admin: "Admin User Login",
        center: "Exam Center User Login",
      };
      return titles[this.module] || "Admin User Login";
    },
    errorLogMsg() {
      return this.$store.getters.getErrorLog || {};
    },
  },

  watch: {
    errorLogMsg(val) {
      if (val && val.message) {
        this.snackbar = true;
      }
    },
  },

  mounted() {
    this.module = this.$route.query.module || "";
    this.loadCaptcha();
  },

  methods: {
    loadCaptcha() {
      this.$store.dispatch("loadCaptcha").then((data) => {
        this.user.key = data.key;
        this.user.captcha = "";
        this.captchaError = false;
      });
    },

    forgotPassword() {
      this.$router.push("/forgot-password");
    },

    validateCaptcha() {
      const val = this.user.captcha && this.user.captcha.trim();
      if (!val) {
        this.captchaErrorText = "Security Verification is required.";
        this.captchaError = true;
        return false;
      }
      if (!/^\d+$/.test(val)) {
        this.captchaErrorText = "Answer must be a number.";
        this.captchaError = true;
        return false;
      }
      this.captchaError = false;
      return true;
    },

    checkUser() {
      const formValid = this.$refs.loginForm.validate();
      const captchaValid = this.validateCaptcha();

      if (!formValid || !captchaValid) return;

      this.errorMessage = "";
      this.loader = true;

      this.$store
        .dispatch("authenticateUser", this.user)
        .then((rsp) => {
          console.log("Authentication successful:", rsp);
          this.loader = false;
          if (this.getAuthUser.user_type == 2) {
            this.setCorrectCenter();
          }
          this.goTo();
        })
        .catch((error) => {
          this.loader = false;
          var response = error && error.response && error.response.data;
          var msg =
            (response && (response.message || response.error)) ||
            "Invalid credentials. Please try again.";
          this.errorMessage = msg;

          this.user.password = "";
          this.loadCaptcha();
        });
    },

    goTo() {
      if (this.ifCenterUser) {
        this.$router.push("/center/center-dashboard");
      } else if (this.ifInvigilatorUser) {
        this.$router.push("/invigilator-dashboard");
      } else if (this.ifRegionalUser) {
        this.$router.push("/regional-center-dashboard");
      } else if (this.ifRescrutinerUser) {
        this.$router.push("/rescrutiny-std-list");
      } else if (this.ifExaminerUser) {
        this.$router.push("/examiner/examiner-dashboard");
      } else if (this.ifHeadExaminerUser) {
        this.$router.push("/examiner-by-head-examiner");
      } else if (this.ifDirectorICTUser) {
        this.$router.push("/result-approval");
      } else if (this.ifSeniorAnalystUser) {
        this.$router.push("/result-approval");
      } else if (this.ifITUser) {
        this.$router.push("/dashboard");
      } else if (this.ifControllerUser) {
        this.$router.push("/result-approval");
      } else if (this.ifVcUser) {
        this.$router.push("/result-approval");
      } else if (this.ifAdminUserType || this.ifControllerUser) {
        this.$router.push("/dashboard");
      } else {
        this.$router.push("/home");
      }
    },

    setCorrectCenter() {
      const payload = {
        type: this.getAuthUser.type == 103 ? 2 : 1,
        center_list_id: this.getAuthUser.center.id,
        exam_initiate_id: this.getExamInitiateId,
      };
      this.$store.dispatch("setCorrectCenter", payload);
    },
  },
};
</script>

<style scoped>
.custom-snackbar {
  border-radius: 10px;
  min-width: 320px;
  max-width: 450px;
  padding: 8px 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}

.snackbar-message {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  word-break: break-word;
}
/* ════════════════════════════════════════════
   CSS Custom Properties — single source of truth
   ════════════════════════════════════════════ */
.login-page {
  --brand: #3f51b5;
  --brand-light: rgba(63, 81, 181, 0.12);
  --bg-page: #eceef6;
  --bg-field: #f5f6fa;
  --border: #e0e2ed;
  --border-hover: #9fa3c0;
  --label: #7b7e8c;
  --radius-card: 18px;
  --radius-field: 8px;
  --field-min-h: 42px;

  --badge-size: 104px;
  --badge-offset: -52px;
  --badge-img: 88px;

  --card-px: 20px;
  --card-pt: 24px;
  --card-pb: 8px;
  --actions-px: 20px;
  --actions-pt: 12px;
  --actions-pb: 16px;

  background-color: var(--bg-page);
  min-height: 100vh;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  padding-left: env(safe-area-inset-left, 0px);
  padding-right: env(safe-area-inset-right, 0px);
}

/* ════════════════════════════════════════════
   Flex wrapper
   ════════════════════════════════════════════ */
.login-flex {
  max-width: 480px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  padding-left: 12px;
  padding-right: 12px;
}

/* ════════════════════════════════════════════
   Card
   ════════════════════════════════════════════ */
.login-card {
  border-radius: var(--radius-card) !important;
  overflow: visible !important;
  width: 100%;
}

/* ════════════════════════════════════════════
   Toolbar
   ════════════════════════════════════════════ */
.login-toolbar {
  border-radius: var(--radius-card) var(--radius-card) 0 0 !important;
  padding-bottom: 20px !important;
  width: 100% !important;
}

.top-title-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: -10px;
}

.top-label {
  font-size: 10px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.55);
  letter-spacing: 1.2px;
  text-transform: uppercase;
  margin-top: 18px;
}

.top-title {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
}

/* ════════════════════════════════════════════
   Logo badge
   ════════════════════════════════════════════ */
.logo-badge-wrapper {
  display: flex;
  justify-content: flex-end;
  padding-right: 20px;
  margin-top: var(--badge-offset);
  position: relative;
  z-index: 2;
}

.logo-badge {
  width: var(--badge-size);
  height: var(--badge-size);
  background: #e6e5e5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid var(--brand);
  box-shadow: 0 0 0 3px #fff, 0 4px 16px rgba(63, 81, 181, 0.25);
  overflow: hidden;
  flex-shrink: 0;
}

.logo-img {
  width: 110px !important;
  height: 110px !important;
}

/* ════════════════════════════════════════════
   Card body spacing
   ════════════════════════════════════════════ */
.login-card-text {
  padding: var(--card-pt) var(--card-px) var(--card-pb) !important;
}

.login-card-actions {
  padding: var(--actions-pt) var(--actions-px) var(--actions-pb) !important;
}

/* ════════════════════════════════════════════
   Field labels
   ════════════════════════════════════════════ */
.field-label {
  color: var(--label);
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  font-size: clamp(10px, 2.5vw, 11px);
  line-height: 1.4;
}

.captcha-label {
  margin-bottom: -24px !important;
}

/* ════════════════════════════════════════════
   Text fields
   ════════════════════════════════════════════ */
.login-field >>> .v-input__slot {
  border: 1px solid var(--border) !important;
  border-radius: var(--radius-field) !important;
  transition: border-color 0.15s, box-shadow 0.15s;
  min-height: var(--field-min-h) !important;
}

.login-field >>> .v-input__slot:hover {
  border-color: var(--border-hover) !important;
}

.login-field.v-input--is-focused >>> .v-input__slot {
  border-color: var(--brand) !important;
  box-shadow: 0 0 0 3px var(--brand-light) !important;
}

/* Error state border on captcha input */
.captcha-input--error >>> .v-input__slot {
  border-color: #f44336 !important;
}

.login-field >>> .v-text-field__slot input {
  font-size: clamp(13px, 3.5vw, 14px);
}

.login-field >>> .v-text-field__slot input::placeholder {
  color: #b0b3c6;
  font-size: clamp(12px, 3vw, 13px);
}

.login-field >>> .v-input__slot::before,
.login-field >>> .v-input__slot::after {
  display: none !important;
}

.login-field >>> .v-messages__message {
  color: #f44336;
  font-size: clamp(10.5px, 2.8vw, 11.5px);
  line-height: 1.4;
}

/* ════════════════════════════════════════════
   Error alert
   ════════════════════════════════════════════ */
.error-alert {
  font-size: clamp(11.5px, 3vw, 13px) !important;
  border-radius: 6px !important;
}

/* ════════════════════════════════════════════
   Captcha row
   ════════════════════════════════════════════ */
.captcha-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.captcha-image-box {
  flex: 0 0 auto;
  width: clamp(100px, 38%, 160px);
  height: 43px;
  border-radius: var(--radius-field);
  border: 1px solid var(--border);
  background: #f0f1f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: border-color 0.15s;
  margin-top: 26px;
}

/* Red border on captcha image box when error */
.captcha-image-box--error {
  border-color: #f44336 !important;
}

.captcha-img {
  max-width: 100%;
  height: 54px;
  object-fit: contain;
}

.captcha-placeholder {
  font-size: 11px;
  color: #aaa;
}

.captcha-equals {
  font-size: clamp(18px, 5vw, 22px);
  font-weight: 400;
  flex-shrink: 0;
  margin-bottom: -30px;
  user-select: none;
}

.captcha-input {
  flex: 1 1 0 !important;
  min-width: 0;
  margin-top: 30px;
}

.captcha-refresh {
  flex-shrink: 0;
  width: 38px !important;
  height: 38px !important;
  margin-top: 28px !important;
}

/* ════════════════════════════════════════════
   Captcha error message — below the full row
   ════════════════════════════════════════════ */
.captcha-error-msg {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #f44336;
  font-size: clamp(10.5px, 2.8vw, 11.5px);
  line-height: 1.4;
  margin-top: 4px;
  margin-left: 2px;
}

.captcha-error-icon {
  font-size: 13px !important;
  flex-shrink: 0;
}

/* ════════════════════════════════════════════
   Hint box
   ════════════════════════════════════════════ */
.hint-box {
  background: #e8eaf6;
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
}

.hint-box--pushed {
  margin-top: 10px;
}

.hint-icon {
  color: #3f51b5 !important;
  font-size: 15px !important;
  flex-shrink: 0;
  margin-top: 1px;
}

.hint-text {
  font-size: 12px;
  color: #3949ab;
  line-height: 1.5;
}

/* ════════════════════════════════════════════
   Login button
   ════════════════════════════════════════════ */
.login-btn {
  height: clamp(40px, 10vw, 44px) !important;
  border-radius: var(--radius-field) !important;
  font-size: clamp(13px, 3.5vw, 14px) !important;
  font-weight: 500 !important;
  letter-spacing: 0.8px !important;
  text-transform: uppercase !important;
  width: 100%;
  padding: 10px;
  margin-top: 5px !important;
  margin-bottom: 14px !important;
}

/* ════════════════════════════════════════════
   Instruction card
   ════════════════════════════════════════════ */
.instruction-card {
  border-radius: 8px !important;
  border: 1px solid var(--border) !important;
  background: #fff !important;
}

.instruction-table {
  width: 100%;
  border-collapse: collapse;
}

.instruction-table td {
  padding: 6px 4px;
  vertical-align: middle;
}

.instruction-text {
  font-size: clamp(11px, 2.8vw, 12px);
  line-height: 1.5;
  word-break: break-word;
}

.instruction-link-cell {
  white-space: nowrap;
  padding-left: 8px;
}

.instruction-link {
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  font-size: clamp(11px, 2.8vw, 12px);
}

.instruction-link:hover {
  text-decoration: underline;
}

/* ════════════════════════════════════════════
   BREAKPOINTS
   ════════════════════════════════════════════ */

/* Very small phones (320–374px) */
@media (max-width: 374px) {
  .login-page {
    --badge-size: 76px;
    --badge-offset: -38px;
    --badge-img: 62px;
    --card-px: 14px;
    --card-pt: 18px;
    --actions-px: 14px;
  }

  .login-flex {
    padding-left: 6px;
    padding-right: 6px;
  }

  .login-toolbar {
    padding-bottom: 14px !important;
  }

  .captcha-image-box {
    width: 88px;
    height: 38px;
  }

  .captcha-img {
    height: 38px;
  }

  .captcha-row {
    gap: 4px;
  }

  .captcha-equals {
    font-size: 16px;
  }
}

/* Normal phones (375–479px) */
@media (min-width: 375px) and (max-width: 479px) {
  .login-page {
    --badge-size: 88px;
    --badge-offset: -44px;
    --badge-img: 72px;
    --card-px: 16px;
    --card-pt: 20px;
    --actions-px: 16px;
  }

  .captcha-image-box {
    width: 110px;
    height: 42px;
  }

  .captcha-img {
    height: 42px;
  }
}

/* Large phones / small tablets portrait (480–599px) */
@media (min-width: 480px) and (max-width: 599px) {
  .login-page {
    --badge-size: 96px;
    --badge-offset: -48px;
    --badge-img: 80px;
    --card-px: 20px;
  }
}

/* Tablet portrait (600–839px) */
@media (min-width: 600px) and (max-width: 839px) {
  .login-page {
    --badge-size: 104px;
    --badge-offset: -52px;
    --badge-img: 88px;
    --card-px: 24px;
    --card-pt: 28px;
    --actions-px: 24px;
    --actions-pb: 20px;
  }

  .login-flex {
    padding-left: 20px;
    padding-right: 20px;
  }
}

/* Tablet landscape / small laptop (840–1023px) */
@media (min-width: 840px) and (max-width: 1023px) {
  .login-page {
    --card-px: 28px;
    --actions-px: 28px;
  }
}

/* Desktop and larger (1024px+) */
@media (min-width: 1024px) {
  .login-page {
    --card-px: 28px;
    --card-pt: 28px;
    --actions-px: 28px;
    --actions-pb: 20px;
  }
}

/* Large desktop / 4K (1920px+) */
@media (min-width: 1920px) {
  .login-page {
    --badge-size: 116px;
    --badge-offset: -58px;
    --badge-img: 98px;
    --card-px: 32px;
    --card-pt: 32px;
    --actions-px: 32px;
    --actions-pb: 24px;
  }

  .login-flex {
    max-width: 520px;
  }
}

/* Landscape orientation on phones */
@media (max-height: 500px) and (orientation: landscape) {
  .login-page {
    --badge-size: 72px;
    --badge-offset: -36px;
    --badge-img: 58px;
    --card-pt: 12px;
    --card-pb: 4px;
    --actions-pt: 8px;
    --actions-pb: 12px;
  }

  .login-toolbar {
    padding-bottom: 10px !important;
  }

  .field-label {
    margin-bottom: 2px !important;
  }

  .login-flex {
    padding-top: 8px;
    padding-bottom: 8px;
  }
}

/* High-DPI / Retina */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .login-field >>> .v-input__slot {
    border-width: 0.5px !important;
  }

  .logo-badge {
    border-width: 3px;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .login-field >>> .v-input__slot {
    transition: none !important;
  }
}

/* Print */
@media print {
  .login-page {
    background: #fff;
    min-height: auto;
  }

  .logo-badge,
  .captcha-row,
  .hint-box,
  .login-btn {
    display: none !important;
  }
}
</style>
